import { useToast } from '@/hooks/useToast';
import { mediaService } from '@/services/mediaService';
import { templatesService } from '@/services/templatesService';
import { whatsappConfigService } from '@/services/whatsappConfigService';
import { getInitials } from '@/utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SendTemplateModal from './SendTemplateModal';

/**
 * ChatConversation component for displaying and managing conversations
 * @param {Object} props - Component props
 * @param {Object} props.contact - Selected contact object
 * @param {Object} props.conversation - Selected conversation object (includes lastMessageAt, lastMessageDirection)
 * @param {Array} props.messages - Array of message objects
 * @param {Function} props.onSendMessage - Callback when sending a message
 * @param {Function} props.onSendTemplateMessage - Callback when sending a template message
 * @param {Function} props.onTyping - Callback for typing events
 * @param {Array} props.typingUsers - Array of users currently typing
 * @param {string} props.projectId - Project ID for fetching templates
 * @returns {JSX.Element} ChatConversation component
 */
const ChatConversation = ({
  contact,
  conversation,
  messages = [],
  onSendMessage,
  onSendTemplateMessage,
  onTyping,
  projectId,
  pagination,
  onLoadMore,
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Media Attachment State
  const [selectedFile, setSelectedFile] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null); // 'image', 'video', 'audio', 'document'

  // 24-hour window and template state
  const [isWithin24Hours, setIsWithin24Hours] = useState(true);
  const [showTemplateMode, setShowTemplateMode] = useState(false);
  const [templates, setTemplates] = useState([]);

  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollPositionRef = useRef(null);
  const lastMessageIdRef = useRef(null);

  // Auto-scroll to bottom when NEW messages arrive at the end
  useEffect(() => {
    if (!messages || messages.length === 0) {
      lastMessageIdRef.current = null;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const currentLastMessageId = lastMessage?._id || lastMessage?.id;
    const prevLastMessageId = lastMessageIdRef.current;

    // Only auto-scroll if:
    // 1. It's the first load (prevLastMessageId === null)
    // 2. A new message was added at the bottom (last message ID changed)
    const isNewMessageAtBottom =
      prevLastMessageId && currentLastMessageId !== prevLastMessageId;

    if (
      (!prevLastMessageId || isNewMessageAtBottom) &&
      messagesEndRef.current &&
      !scrollPositionRef.current
    ) {
      // Use 'auto' for instant scroll instead of 'smooth'
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }

    lastMessageIdRef.current = currentLastMessageId;
  }, [messages]);

  // Handle conversation change and scroll to bottom
  useEffect(() => {
    if (conversation?._id && messagesEndRef.current) {
      // Use a small timeout to ensure the content is rendered
      const timeoutId = setTimeout(() => {
        messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  }, [conversation?._id]);

  // Auto-focus the input when switching to an active (within-24h) conversation
  useEffect(() => {
    if (conversation?._id && isWithin24Hours && textareaRef.current) {
      const focusId = setTimeout(() => {
        textareaRef.current?.focus();
      }, 80);
      return () => clearTimeout(focusId);
    }
  }, [conversation?._id, isWithin24Hours]);

  // Handle scroll stabilization when prepending messages
  useLayoutEffect(() => {
    if (scrollPositionRef.current && scrollContainerRef.current) {
      const { previousScrollHeight, previousScrollTop } =
        scrollPositionRef.current;
      const currentScrollHeight = scrollContainerRef.current.scrollHeight;

      // Adjust scroll position: current position + height difference
      const heightDifference = currentScrollHeight - previousScrollHeight;
      scrollContainerRef.current.scrollTop =
        previousScrollTop + heightDifference;

      // Note: We leave scrollPositionRef.current for one cycle to allow useEffect to skip auto-scroll
      setTimeout(() => {
        scrollPositionRef.current = null;
      }, 100);
    }
  }, [messages]);

  const handleLoadMoreClick = () => {
    if (scrollContainerRef.current) {
      // Capture current state before loading
      scrollPositionRef.current = {
        previousScrollHeight: scrollContainerRef.current.scrollHeight,
        previousScrollTop: scrollContainerRef.current.scrollTop,
      };
    }
    onLoadMore();
  };

  // Handle typing indicator
  useEffect(() => {
    if (newMessage.length > 0) {
      onTyping?.(true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set timeout to stop typing indicator
      typingTimeoutRef.current = setTimeout(() => {
        onTyping?.(false);
      }, 2000);
    } else {
      onTyping?.(false);
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [newMessage, onTyping]);

  const { showError } = useToast();

  // Check 24-hour messaging window
  useEffect(() => {
    if (!conversation) {
      setIsWithin24Hours(false);
      return;
    }

    // Check if last message was from customer (inbound)
    if (!conversation.lastInboundMessageAt) {
      // If last message was outbound or no messages, cannot send regular messages
      setIsWithin24Hours(false);
      return;
    }

    // Calculate time difference from last inbound message
    const lastMessageTime = new Date(conversation.lastInboundMessageAt);
    const now = new Date();
    const hoursDiff = (now - lastMessageTime) / (1000 * 60 * 60);

    // Within 24 hours if less than 24 hours have passed
    setIsWithin24Hours(hoursDiff < 24);
  }, [conversation]);

  // Fetch approved templates for template messaging
  useEffect(() => {
    const fetchTemplates = async () => {
      if (!projectId) return;

      try {
        const response = await templatesService.getTemplates(projectId, {
          limit: 100,
        });

        if (response.docs) {
          // Filter only approved templates
          const approvedTemplates = response.docs.filter(
            template => template.status === 'APPROVED'
          );
          setTemplates(approvedTemplates);
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        showError('Failed to load templates');
      }
    };

    fetchTemplates();
  }, [projectId, showError]);

  // Fetch WhatsApp configuration for media upload
  const { data: whatsappConfigData } = useQuery({
    queryKey: ['whatsapp-config', projectId],
    queryFn: () => whatsappConfigService.getWhatsAppConfig(projectId),
    enabled: !!projectId,
  });

  const whatsappConfig =
    whatsappConfigData?.data?.whatsappConfig ||
    whatsappConfigData?.whatsappConfig;
  const phoneNumberId = whatsappConfig?.phoneNumberId;

  const handleSendMessage = async e => {
    if (e) e.preventDefault();

    if (selectedFile) {
      if (!phoneNumberId) {
        showError('WhatsApp configuration missing. Cannot upload media.');
        return;
      }

      setIsUploading(true);
      try {
        const mediaResponse = await mediaService.uploadWhatsAppMedia(
          projectId,
          phoneNumberId,
          selectedFile
        );
        const mediaId = mediaResponse?.data?.id;
        const key = mediaResponse?.data?.key;

        if (!mediaId) throw new Error('Failed to get media ID from WhatsApp');

        const messageData = {
          type: attachmentType,
        };

        if (attachmentType === 'image') {
          messageData.image = {
            id: mediaId,
            key,
            caption: newMessage || undefined,
          };
        } else if (attachmentType === 'video') {
          messageData.video = {
            id: mediaId,
            key,
            caption: newMessage || undefined,
          };
        } else if (attachmentType === 'audio') {
          messageData.audio = {
            id: mediaId,
            key,
          };
        } else if (attachmentType === 'document') {
          messageData.document = {
            id: mediaId,
            key,
            caption: newMessage || undefined,
            filename: selectedFile.name,
          };
        }

        await onSendMessage(messageData);

        // Clear state
        setSelectedFile(null);
        setAttachmentType(null);
        setNewMessage('');
        setShowEmojiPicker(false);
      } catch (error) {
        console.error('Media upload failed:', error);
        showError(error.message || 'Failed to upload and send media');
      } finally {
        setIsUploading(false);
      }
      return;
    }

    if (newMessage.trim() && onSendMessage) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
      setShowEmojiPicker(false);
      onTyping?.(false);
    }
  };

  const handleFileSelect = (event, type) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setAttachmentType(type);
      setShowAttachmentMenu(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSendMessage(e);
    }
  };

  const startRecording = () => {
    setIsRecording(true);
    setRecordingTime(0);
    recordingIntervalRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);
  };

  const stopRecording = () => {
    if (!isRecording) return;

    setIsRecording(false);
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
    }

    // Only send if there's actual recording time (at least 1 second)
    if (recordingTime > 0) {
      const voiceMessage = {
        type: 'voice',
        content: `Voice message (${Math.floor(recordingTime / 60)}:${(recordingTime % 60).toString().padStart(2, '0')})`,
        duration: recordingTime,
      };
      onSendMessage(voiceMessage);
    }

    setRecordingTime(0);
  };

  const formatRecordingTime = seconds => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Helper to format dates for headers
   */
  const formatDateLabel = dateString => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString([], {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  };

  /**
   * Helper to group messages by date
   */
  const groupMessagesByDate = msgs => {
    if (!msgs || msgs.length === 0) return [];

    const groups = [];
    msgs.forEach(message => {
      const dateLabel = formatDateLabel(message.timestamp);
      const lastGroup = groups[groups.length - 1];

      if (lastGroup && lastGroup.label === dateLabel) {
        lastGroup.messages.push(message);
      } else {
        groups.push({
          label: dateLabel,
          messages: [message],
        });
      }
    });
    return groups;
  };

  /**
   * Custom Audio Player for WhatsApp-style audio messages
   */
  const AudioPlayer = ({ src, isOutbound, onDownload }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = useRef(null);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const setAudioData = () => {
        setDuration(audio.duration);
      };

      const setAudioTime = () => {
        setCurrentTime(audio.currentTime);
        setProgress((audio.currentTime / audio.duration) * 100);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
      };

      audio.addEventListener('loadedmetadata', setAudioData);
      audio.addEventListener('timeupdate', setAudioTime);
      audio.addEventListener('ended', handleEnded);

      return () => {
        audio.removeEventListener('loadedmetadata', setAudioData);
        audio.removeEventListener('timeupdate', setAudioTime);
        audio.removeEventListener('ended', handleEnded);
      };
    }, []);

    const togglePlay = () => {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    };

    const handleProgressChange = e => {
      const newProgress = parseFloat(e.target.value);
      const newTime = (newProgress / 100) * duration;
      audioRef.current.currentTime = newTime;
      setProgress(newProgress);
    };

    return (
      <div className="flex items-center space-x-3 p-2 min-w-[280px]">
        <audio ref={audioRef} src={src} />

        <button
          onClick={togglePlay}
          className={`flex-shrink-0 w-8 h-8 flex items-center justify-center transition-colors ${
            isOutbound ? 'text-gray-600' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          {isPlaying ? (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="flex-1 flex flex-col space-y-1">
          <div className="relative w-full h-1.5 flex items-center group">
            <input
              type="range"
              min="0"
              max="100"
              value={progress || 0}
              onChange={handleProgressChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            <div
              className={`w-full h-1 rounded-full ${
                isOutbound ? 'bg-black/10' : 'bg-gray-100'
              }`}
            >
              <div
                className={`h-full rounded-full relative ${
                  isOutbound ? 'bg-sky-500' : 'bg-sky-500'
                }`}
                style={{ width: `${progress}%` }}
              >
                <div
                  className={`absolute right-[-4px] top-1/2 transform -translate-y-1/2 w-3 h-3 rounded-full shadow-sm ${
                    isOutbound ? 'bg-sky-500' : 'bg-sky-500'
                  }`}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-start items-center px-0.5">
            <span
              className={`text-[10px] leading-none ${
                isOutbound ? 'text-gray-500' : 'text-gray-400'
              }`}
            >
              {formatRecordingTime(Math.floor(currentTime))}
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {onDownload && (
            <button
              onClick={onDownload}
              className={`p-1 rounded-full transition-colors ${
                isOutbound
                  ? 'text-gray-500 hover:bg-black/5'
                  : 'text-gray-300 hover:text-gray-500'
              }`}
              title="Download"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
            </button>
          )}
          <div
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isOutbound ? 'bg-black/5' : 'bg-orange-400'
            }`}
          >
            <svg
              className={`w-5 h-5 ${isOutbound ? 'text-gray-500' : 'text-white'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 18v-6a9 9 0 0118 0v6M3 18a2 2 0 002 2h2a2 2 0 002-2v-5a2 2 0 00-2-2H5a2 2 0 01-2 2v5zm14 0a2 2 0 002 2h2a2 2 0 002-2v-5a2 2 0 00-2-2h-2a2 2 0 00-2 2v5z"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  };

  const renderMessage = message => {
    const isOutbound = message.direction === 'outbound';
    const type = message.messageType || message.type || 'text';
    const originalData = message.originalMessageData || {};

    const getMediaUrl = () => {
      // Check for file object first (from internal storage)
      if (message.file?.url) return message.file.url;

      // Check for WhatsApp media data in messageData and originalMessageData
      const mediaData = message.messageData?.[type] || originalData?.[type];
      return (
        mediaData?.signedUrl ||
        mediaData?.link ||
        mediaData?.url ||
        message.content
      );
    };

    const getCaption = () => {
      if (type === 'text') return message.content;
      const mediaData = originalData[type];
      return mediaData?.caption || message.content;
    };

    const renderContextMessage = () => {
      const contextMsg = message.contextMessage;
      if (!contextMsg) return null;

      const ctxType = contextMsg.messageData?.type || contextMsg.type || 'text';
      const isCtxOutbound = contextMsg.direction === 'outbound';

      let previewText = '';
      let previewMedia = null;

      if (ctxType === 'text') {
        previewText =
          contextMsg.content || contextMsg.messageData?.text?.body || '';
      } else if (ctxType === 'image') {
        previewText = 'Image';
        const imgUrl =
          contextMsg.messageData?.image?.url ||
          contextMsg.file?.url ||
          contextMsg.messageData?.image?.signedUrl;
        if (imgUrl) {
          previewMedia = (
            <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0">
              <img
                src={imgUrl}
                alt="Context"
                className="w-full h-full object-cover"
              />
            </div>
          );
        }
      } else if (ctxType === 'video') {
        previewText = 'Video';
      } else if (ctxType === 'document' || ctxType === 'file') {
        previewText =
          contextMsg.messageData?.document?.filename ||
          contextMsg.messageData?.file?.filename ||
          'Document';
      } else if (ctxType === 'audio' || ctxType === 'voice') {
        previewText = 'Audio';
      } else if (ctxType === 'sticker') {
        previewText = 'Sticker';
      } else if (ctxType === 'template') {
        previewText = 'Template Message';
      } else if (ctxType === 'location') {
        previewText = 'Location';
      } else if (ctxType === 'contacts') {
        previewText = 'Contact';
      } else {
        previewText = 'Message';
      }

      return (
        <div
          className="mb-2 p-2 rounded-md border-l-4 flex items-center justify-between space-x-3 bg-black/5 cursor-default overflow-hidden"
          style={{ borderLeftColor: isCtxOutbound ? '#128C7E' : '#25D366' }}
        >
          <div className="flex-1 min-w-0">
            <span
              className="text-[10px] font-bold block mb-0.5"
              style={{ color: isCtxOutbound ? '#128C7E' : '#25D366' }}
            >
              {isCtxOutbound ? 'You' : contact?.name || 'Contact'}
            </span>
            <p className="text-[11px] text-gray-600 truncate leading-tight">
              {previewText}
            </p>
          </div>
          {previewMedia}
        </div>
      );
    };

    return (
      <div
        key={message._id || message.id}
        className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div
          className={`relative max-w-[350px] rounded-lg ${
            type === 'text' || type === 'button'
              ? isOutbound
                ? 'pl-4 pr-2 py-2'
                : 'pl-2 pr-4 py-2'
              : 'p-0.5'
          } ${
            isOutbound
              ? 'bg-green-200 text-gray-900 border border-green-300'
              : 'bg-white text-gray-900 border border-gray-200'
          }`}
        >
          {renderContextMessage()}
          {/* Image Message */}
          {type === 'image' && (
            <div className="mb-2 relative group">
              <img
                src={getMediaUrl()}
                alt={getCaption()}
                className="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                onClick={() => window.open(getMediaUrl(), '_blank')}
                onError={e => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://placehold.co/400x300?text=Image+Unavailable';
                }}
              />
              <button
                onClick={() => window.open(getMediaUrl(), '_blank')}
                className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
                title="Download"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </button>
              {getCaption() && getCaption() !== getMediaUrl() && (
                <p className="text-sm px-2 pt-2">{getCaption()}</p>
              )}
            </div>
          )}
          {/* Sticker Message */}
          {type === 'sticker' && (
            <div className="relative group p-1">
              <img
                src={getMediaUrl()}
                alt="Sticker"
                className="rounded-lg w-32 h-32 object-contain cursor-pointer transition-transform hover:scale-110"
                onClick={() => window.open(getMediaUrl(), '_blank')}
                onError={e => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://placehold.co/128x128?text=Sticker+Unavailable';
                }}
              />
            </div>
          )}
          {/* Location Message */}
          {type === 'location' &&
            (() => {
              const locationData =
                message.messageData?.location || originalData?.location || {};
              const { latitude, longitude, name, address } = locationData;
              // Generate a simple Google Maps static image URL (requires no API key for basic usage in some cases, but better to use a simple preview or link)
              // Since we don't have a key, we'll use a nice placeholder and a link or a public static map service if possible.
              // A simple approach is to use a Google Maps Embed or just a link with a map icon.
              const mapUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;

              return (
                <div
                  className="p-3 w-64 cursor-pointer hover:bg-black/5 transition-colors"
                  onClick={() => window.open(mapUrl, '_blank')}
                >
                  <div className="flex items-center space-x-2 mb-2 text-primary-600 font-medium">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="text-sm">Location</span>
                  </div>
                  <div className="bg-gray-100 rounded-lg h-32 flex items-center justify-center mb-2 overflow-hidden border border-gray-200">
                    <img
                      src={`https://maps.locationiq.com/v3/staticmap?key=pk.aa60882e7054ae46083049533362141a&center=${latitude},${longitude}&zoom=15&size=400x200&markers=icon:large-red-cutout|${latitude},${longitude}`}
                      alt="Map Preview"
                      className="w-full h-full object-cover"
                      onError={e => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML =
                          '<div class="text-xs text-gray-500 text-center px-4">Map preview unavailable. Click to view on Google Maps.</div>';
                      }}
                    />
                  </div>
                  {name && (
                    <p className="text-sm font-semibold truncate">{name}</p>
                  )}
                  {address && (
                    <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                      {address}
                    </p>
                  )}
                  {!name && !address && (
                    <p className="text-xs text-gray-500 italic mt-1">
                      {latitude.toFixed(6)}, {longitude.toFixed(6)}
                    </p>
                  )}
                </div>
              );
            })()}
          {/* Contacts Message */}
          {type === 'contacts' &&
            (() => {
              const contacts =
                message.messageData?.contacts || originalData?.contacts || [];
              return (
                <div className="p-2 w-64 divide-y divide-gray-100">
                  {contacts.map((contact, idx) => (
                    <div key={idx} className={`${idx > 0 ? 'pt-3 mt-3' : ''}`}>
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-6 h-6 text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {contact.name?.formatted_name ||
                              `${contact.name?.first_name || ''} ${contact.name?.last_name || ''}`.trim() ||
                              'Unknown Contact'}
                          </p>
                          <p className="text-xs text-gray-500">Contact</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {contact.phones?.map((phone, pIdx) => (
                          <div
                            key={pIdx}
                            className="flex items-center justify-between group/phone"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-700 truncate">
                                {phone.phone}
                              </p>
                              {phone.type && (
                                <p className="text-[10px] text-gray-400 uppercase">
                                  {phone.type}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => {
                                const waId =
                                  phone.wa_id ||
                                  phone.phone?.replace(/\D/g, '');
                                if (waId) {
                                  window.open(
                                    `https://wa.me/${waId}`,
                                    '_blank'
                                  );
                                }
                              }}
                              className="p-1 text-primary-600 hover:bg-primary-50 rounded opacity-0 group-hover/phone:opacity-100 transition-opacity"
                              title="Message"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          {/* Video Message */}
          {type === 'video' && (
            <div className="mb-2 relative group">
              <video
                src={getMediaUrl()}
                controls
                className="rounded-lg max-w-full h-auto"
              />
              <button
                onClick={() => window.open(getMediaUrl(), '_blank')}
                className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70 z-10"
                title="Download"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </button>
              {getCaption() && getCaption() !== getMediaUrl() && (
                <p className="text-sm px-2 pt-2">{getCaption()}</p>
              )}
            </div>
          )}
          {/* Audio/Voice Message */}
          {(type === 'audio' || type === 'voice') && (
            <AudioPlayer
              src={getMediaUrl()}
              isOutbound={isOutbound}
              onDownload={() => window.open(getMediaUrl(), '_blank')}
            />
          )}
          {/* Document/File Message */}
          {(type === 'document' || type === 'file') &&
            (() => {
              const mediaData =
                message.messageData?.[type] || originalData?.[type];
              const filename = (
                mediaData?.filename ||
                message.file?.name ||
                message.content ||
                'Document'
              ).toLowerCase();

              let docStyles = {
                iconColor: isOutbound
                  ? 'bg-green-300 text-green-800'
                  : 'bg-primary-100 text-primary-600',
                bgColor: isOutbound
                  ? 'bg-white/50 border-green-300'
                  : 'bg-gray-50 border-gray-200',
                textColor: 'text-gray-900',
                linkTextColor: isOutbound
                  ? 'text-green-700'
                  : 'text-primary-600',
                icon: (
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                ),
              };

              if (filename.endsWith('.pdf')) {
                docStyles = {
                  ...docStyles,
                  iconColor: 'bg-red-50',
                  linkTextColor: 'text-red-600',
                  icon: (
                    <img
                      src="/pdf.png"
                      alt="PDF"
                      className="w-8 h-8 object-contain"
                    />
                  ),
                };
              } else if (
                filename.endsWith('.doc') ||
                filename.endsWith('.docx')
              ) {
                docStyles = {
                  ...docStyles,
                  iconColor: 'bg-blue-50',
                  linkTextColor: 'text-blue-600',
                  icon: (
                    <img
                      src="/docx.png"
                      alt="Word"
                      className="w-8 h-8 object-contain"
                    />
                  ),
                };
              } else if (
                filename.endsWith('.xls') ||
                filename.endsWith('.xlsx') ||
                filename.endsWith('.csv')
              ) {
                docStyles = {
                  ...docStyles,
                  iconColor: 'bg-green-50',
                  linkTextColor: 'text-green-600',
                  icon: (
                    <img
                      src="/xlsx.png"
                      alt="Excel"
                      className="w-8 h-8 object-contain"
                    />
                  ),
                };
              }

              return (
                <div className="mb-2">
                  <div
                    className={`flex items-center space-x-3 p-3 rounded-lg border ${docStyles.bgColor} ${docStyles.textColor}`}
                  >
                    <div className={`p-2 rounded-lg ${docStyles.iconColor}`}>
                      {docStyles.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {mediaData?.filename ||
                          message.file?.name ||
                          message.content ||
                          'Document'}
                      </p>
                      <a
                        href={getMediaUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-xs hover:underline mt-1 block ${docStyles.linkTextColor}`}
                      >
                        View / Download
                      </a>
                    </div>
                  </div>
                  {getCaption() && getCaption() !== getMediaUrl() && (
                    <p className="text-sm px-2 pt-2">{getCaption()}</p>
                  )}
                </div>
              );
            })()}
          {/* Text Message */}
          {type === 'text' && (
            <p className="text-sm border-0 bg-transparent whitespace-pre-wrap">
              {message.content}
            </p>
          )}
          {/* Button Message (Quick Reply) */}
          {type === 'button' && (
            <p className="text-sm border-0 bg-transparent whitespace-pre-wrap">
              {message?.messageData?.button?.text}
            </p>
          )}
          {/* Template Message */}
          {type === 'template' &&
            (() => {
              const templateData =
                message.messageData?.template || originalData?.template || {};
              const templateMetadata = message.metadata?.template;
              const templateSchema = templateMetadata?.schema;
              const templatePayload = templateMetadata?.payload;
              const templateName =
                templateSchema?.name ||
                templatePayload?.name ||
                templateData.name;

              // Use schema from metadata if available, otherwise fallback to templates state
              const template =
                templateSchema || templates.find(t => t.name === templateName);

              if (!template) {
                return (
                  <div className="p-2">
                    <div className="flex items-center space-x-2 text-amber-600 mb-1">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-xs font-bold uppercase tracking-wider">
                        Template Message
                      </span>
                    </div>
                    <p className="text-sm font-medium">{templateName}</p>
                    <p className="text-xs text-gray-500 italic mt-1">
                      Template definition not available
                    </p>
                  </div>
                );
              }

              // Extract values for dynamic parameters if they exist
              // Prioritize payload components for actual values sent
              const components =
                templatePayload?.components || templateData.components || [];

              const getParamValue = (compType, index) => {
                const component = components.find(
                  c => c.type === compType || c.type?.toLowerCase() === compType
                );
                if (!component || !component.parameters) return null;
                const param = component.parameters[index - 1];
                if (!param) return null;
                // Support both standard and payload structures
                return param.text || param.value || param.code || '';
              };

              const getHeaderMedia = compType => {
                // Priority 1: Use signedUrl from dynamicValues if available (provided by backend)
                const dynamicSignedUrl =
                  message.messageData?.template?.dynamicValues?.header
                    ?.signedUrl;
                if (dynamicSignedUrl) return dynamicSignedUrl;

                // Priority 2: Try payload from metadata
                const component = components.find(
                  c => c.type === 'header' || c.type?.toLowerCase() === 'header'
                );
                if (!component || !component.parameters) return null;

                const param = component.parameters.find(
                  p => p.type === compType || p.type?.toLowerCase() === compType
                );
                if (!param) return null;

                const media = param[compType] || param[compType.toLowerCase()];
                if (!media) return null;

                // Priority 3: signedUrl > link > url > id from metadata
                if (media.signedUrl) return media.signedUrl;
                if (media.link) return media.link;
                if (media.url && media.url.startsWith('http')) return media.url;

                return media.url || media.id;
              };

              const formatWhatsAppTextLocal = text => {
                if (!text) return '';
                return text.split('\n').map((line, i) => {
                  const formattedLine = line
                    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
                    .replace(/_(.*?)_/g, '<em>$1</em>')
                    .replace(/~(.*?)~/g, '<del>$1</del>')
                    .replace(/```(.*?)```/g, '<code>$1</code>');

                  return (
                    <div
                      key={i}
                      dangerouslySetInnerHTML={{ __html: formattedLine }}
                      className="mb-0.5 last:mb-0"
                    />
                  );
                });
              };

              const renderHeader = () => {
                if (!template.header) return null;
                const { format, text } = template.header;

                if (format === 'TEXT') {
                  let headerText = text;
                  // Replace {{1}} if dynamic
                  if (text && text.includes('{{1}}')) {
                    const val = getParamValue('header', 1) || '...';
                    headerText = text.replace(/\{\{1\}\}/g, val);
                  }
                  return (
                    <div className="font-bold text-gray-900 mb-1 border-0 bg-transparent">
                      {formatWhatsAppTextLocal(headerText)}
                    </div>
                  );
                }

                if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
                  const mediaUrl = getHeaderMedia(format.toLowerCase());
                  if (!mediaUrl) {
                    return (
                      <div className="w-full aspect-video bg-gray-100 rounded-md flex items-center justify-center mb-2">
                        <span className="text-xs text-gray-400">
                          {format} Media
                        </span>
                      </div>
                    );
                  }

                  if (format === 'IMAGE') {
                    return (
                      <img
                        src={mediaUrl}
                        className="w-full h-auto rounded-md mb-2 cursor-pointer"
                        alt="Header"
                        onClick={() => window.open(mediaUrl, '_blank')}
                      />
                    );
                  }
                  if (format === 'VIDEO') {
                    return (
                      <video
                        src={mediaUrl}
                        controls
                        className="w-full h-auto rounded-md mb-2"
                      />
                    );
                  }
                  if (format === 'DOCUMENT') {
                    const filename =
                      mediaUrl.split('/').pop()?.split('?')[0] || '';
                    let docStyles = {
                      bgColor: 'bg-gray-50',
                      textColor: 'text-gray-900',
                      iconColor: 'bg-primary-50',
                      linkTextColor: 'text-primary-600',
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      ),
                    };

                    if (filename.toLowerCase().endsWith('.pdf')) {
                      docStyles = {
                        ...docStyles,
                        iconColor: 'bg-red-50',
                        linkTextColor: 'text-red-600',
                        icon: (
                          <img
                            src="/pdf.png"
                            alt="PDF"
                            className="w-8 h-8 object-contain"
                          />
                        ),
                      };
                    } else if (
                      filename.toLowerCase().endsWith('.doc') ||
                      filename.toLowerCase().endsWith('.docx')
                    ) {
                      docStyles = {
                        ...docStyles,
                        iconColor: 'bg-blue-50',
                        linkTextColor: 'text-blue-600',
                        icon: (
                          <img
                            src="/docx.png"
                            alt="Word"
                            className="w-8 h-8 object-contain"
                          />
                        ),
                      };
                    } else if (
                      filename.toLowerCase().endsWith('.xls') ||
                      filename.toLowerCase().endsWith('.xlsx') ||
                      filename.toLowerCase().endsWith('.csv')
                    ) {
                      docStyles = {
                        ...docStyles,
                        iconColor: 'bg-green-50',
                        linkTextColor: 'text-green-600',
                        icon: (
                          <img
                            src="/xlsx.png"
                            alt="Excel"
                            className="w-8 h-8 object-contain"
                          />
                        ),
                      };
                    }

                    return (
                      <div
                        className={`flex items-center space-x-3 p-2 border border-gray-100 rounded-md mb-2 ${docStyles.bgColor}`}
                      >
                        <div className={`p-1.5 rounded ${docStyles.iconColor}`}>
                          {docStyles.icon}
                        </div>
                        <div className="flex-1 truncate">
                          <p className="text-xs font-medium truncate">
                            {filename || 'Document Header'}
                          </p>
                          <a
                            href={mediaUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-[10px] hover:underline ${docStyles.linkTextColor}`}
                          >
                            View
                          </a>
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              };

              const renderBody = () => {
                if (!template.body?.text) return null;
                let bodyText = template.body.text;

                // Replace variables {{1}}, {{2}}, etc.
                const matches = bodyText.match(/\{\{(\d+)\}\}/g);
                if (matches) {
                  matches.forEach(match => {
                    const index = match.replace(/[{}]/g, '');
                    const val = getParamValue('body', parseInt(index)) || match;
                    bodyText = bodyText.replace(
                      new RegExp(`\\{\\{${index}\\}\\}`),
                      val
                    );
                  });
                }

                return (
                  <div className="text-sm text-gray-800 border-0 bg-transparent">
                    {formatWhatsAppTextLocal(bodyText)}
                  </div>
                );
              };

              const renderFooter = () => {
                if (!template.footer?.text) return null;
                return (
                  <div className="text-[11px] text-gray-500 mt-2 border-0 bg-transparent">
                    {template.footer.text}
                  </div>
                );
              };

              const renderButtons = () => {
                if (!template.buttons || template.buttons.length === 0)
                  return null;

                return (
                  <div className="mt-3 space-y-1.5 pt-2 border-t border-gray-100">
                    {template.buttons.map((button, idx) => {
                      let buttonText = button.text;
                      if (button.type === 'copy_code') {
                        // For copy code, usually there is a parameter
                        getParamValue('button', idx + 1);
                        buttonText = buttonText || 'Copy Code';
                      }

                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-center py-1.5 px-3 bg-white border border-gray-100 rounded-md shadow-sm"
                        >
                          <span className="text-xs font-bold text-primary-600">
                            {buttonText}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              };

              return (
                <div className="p-2">
                  <div className="flex items-center space-x-2 text-primary-600 mb-2 opacity-70">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      {templateName}
                    </span>
                  </div>
                  {renderHeader()}
                  {renderBody()}
                  {renderFooter()}
                  {renderButtons()}
                </div>
              );
            })()}
          {/* Unsupported types fallback */}
          {![
            'image',
            'video',
            'audio',
            'voice',
            'document',
            'file',
            'text',
            'template',
            'sticker',
            'location',
            'contacts',
            'button',
          ].includes(type) && (
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm italic text-gray-400">
                Unsupported message type: {type}
              </p>
            </div>
          )}
          {/* Message timestamp and status */}
          <div
            className={`flex items-center justify-end mt-1 space-x-1 ${
              isOutbound ? 'text-gray-500' : 'text-gray-400'
            } ${type === 'audio' || type === 'voice' ? 'px-2 pb-1' : ''}`}
          >
            <span className="text-[10px]">
              {new Date(message.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </span>
            {isOutbound && (
              <span className="ml-1">
                {(() => {
                  const status = message.currentStatus || message.status;
                  const errorMsg =
                    message.failureReason ||
                    message.errorMessage ||
                    message.error;

                  if (status === 'sent') {
                    return (
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    );
                  }
                  if (status === 'delivered') {
                    return (
                      <div className="flex -space-x-3">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    );
                  }
                  if (status === 'read') {
                    return (
                      <div className="flex -space-x-3">
                        <svg
                          className="w-4 h-4 text-sky-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <svg
                          className="w-4 h-4 text-sky-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    );
                  }
                  if (status === 'failed') {
                    const failedEntry = message.statusHistory?.find(
                      h => h.status === 'failed'
                    );
                    const tooltipMsg =
                      failedEntry?.errorMessage ||
                      errorMsg ||
                      'Message failed to send';

                    return (
                      <div className="relative group/status">
                        <div className="flex items-center justify-center w-4 h-4 rounded-full bg-red-100 text-red-600 cursor-help">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        {/* Custom Tooltip */}
                        <div className="absolute bottom-full right-0 mb-2 hidden group-hover/status:block z-50">
                          <div className="bg-gray-900 text-white text-[11px] rounded py-1.5 px-3 whitespace-nowrap shadow-xl max-w-xs break-words">
                            {tooltipMsg}
                            {/* Tooltip Arrow */}
                            <div className="absolute top-full right-1.5 border-4 border-transparent border-t-gray-900" />
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SelectedAttachmentPreview = () => {
    if (!selectedFile) return null;

    const getIcon = () => {
      switch (attachmentType) {
        case 'image':
          return (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          );
        case 'video':
          return (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          );
        case 'audio':
          return (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            </svg>
          );
        case 'document':
          return (
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          );
        default:
          return null;
      }
    };

    return (
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 text-primary-600 rounded-lg">
            {getIcon()}
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
              {selectedFile.name}
            </span>
            <span className="text-xs text-gray-500 capitalize">
              {attachmentType} • {(selectedFile.size / 1024 / 1024).toFixed(2)}{' '}
              MB
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setSelectedFile(null);
            setAttachmentType(null);
          }}
          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    );
  };

  if (!contact) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-primary-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Start a Conversation
          </h3>
          <p className="text-gray-500 mb-4">
            Click "New Chat" in the header to select a contact and start
            messaging
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-sm mx-auto">
            <div className="flex items-center space-x-2">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span className="text-sm text-green-800 font-medium">
                How to start:
              </span>
            </div>
            <ul className="text-sm text-green-700 mt-2 space-y-1 text-left">
              <li>• Click "New Chat" button in the header</li>
              <li>• Select a contact from the modal</li>
              <li>• Begin typing your message</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50 flex flex-col h-full overflow-hidden">
      {/* Conversation Header */}
      <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center space-x-3">
          {contact?.avatar ? (
            <img
              src={contact.avatar}
              alt={contact?.name || 'Contact'}
              className="w-10 h-10 rounded-full"
            />
          ) : (
            <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {getInitials(contact?.name)}
              </span>
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">
              {contact?.name || 'Unknown Contact'}
            </h2>
            <p className="text-sm text-gray-500">
              {contact?.phoneNumber || 'No phone number'}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Audio Call Button */}
            <div className="relative group/tooltip">
              <button
                disabled
                className="p-2 text-gray-300 cursor-not-allowed transition-colors"
                title="Audio call"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </button>
              {/* Tooltip */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover/tooltip:block z-[60]">
                <div className="bg-gray-800 text-white text-[10px] py-1 px-2 rounded whitespace-nowrap shadow-lg">
                  Coming soon
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-bottom-gray-800" />
                </div>
              </div>
            </div>

            {/* Video Call Button */}
            <div className="relative group/tooltip">
              <button
                disabled
                className="p-2 text-gray-300 cursor-not-allowed transition-colors"
                title="Video call"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
              {/* Tooltip */}
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover/tooltip:block z-[60]">
                <div className="bg-gray-800 text-white text-[10px] py-1 px-2 rounded whitespace-nowrap shadow-lg">
                  Coming soon
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-bottom-gray-800" />
                </div>
              </div>
            </div>

            <button className="p-2 text-gray-400 hover:text-gray-600">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area Container - Fixed Background */}
      <div
        className="flex-1 relative overflow-hidden"
        style={{ backgroundColor: '#e5ddd5' }}
      >
        {/* Fixed WhatsApp Pattern Overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url("https://static.xx.fbcdn.net/rsrc.php/v4/yb/r/rmr3BrOAAd8.png")`,
            backgroundSize: '600px',
            backgroundRepeat: 'repeat',
            filter: 'invert(1)',
            opacity: 0.4,
          }}
        />

        {/* Scrollable Messages Content */}
        <div
          ref={scrollContainerRef}
          className="absolute inset-0 p-4 overflow-y-auto z-0"
        >
          <div className="relative z-0">
            {messages.length === 0 ? (
              <div className="text-center text-gray-500 text-sm py-8">
                <div className="w-12 h-12 bg-white/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <p>No messages yet</p>
                <p className="text-xs mt-1">Start the conversation!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {groupMessagesByDate(messages).map((group, groupIndex) => (
                  <div key={group.label} className="space-y-4 text-center">
                    {/* Sticky Date Header */}
                    <div className="sticky top-0 z-20 flex justify-center py-2">
                      <div className="bg-white/80 backdrop-blur-sm border border-gray-100 px-3 py-1 rounded-lg shadow-sm text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        {group.label}
                      </div>
                    </div>

                    {/* Load more button - just below the first sticky header */}
                    {groupIndex === 0 && pagination?.hasNextPage && (
                      <div className="flex justify-center mb-4 pt-2">
                        <button
                          onClick={handleLoadMoreClick}
                          className="bg-white/90 hover:bg-white text-primary-600 px-4 py-1.5 rounded-full shadow-sm border border-primary-100 text-xs font-medium transition-all hover:shadow-md flex items-center space-x-2 group"
                        >
                          <svg
                            className="w-3.5 h-3.5 group-hover:-translate-y-0.5 transition-transform"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 15l7-7 7 7"
                            />
                          </svg>
                          <span>Load older messages</span>
                        </button>
                      </div>
                    )}

                    <div className="space-y-4 text-left">
                      {group.messages.map(renderMessage)}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Bottom Section - Input */}
      <div className="flex-shrink-0">
        {/* Attachment Preview */}
        <SelectedAttachmentPreview />

        {/* Message Input */}
        <div className="bg-white border-t border-gray-200 p-4">
          <form
            onSubmit={handleSendMessage}
            className="flex items-center space-x-2"
          >
            {/* Attachment Button */}
            {isWithin24Hours && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
                  className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                    />
                  </svg>
                </button>

                {/* Attachment Menu */}
                {showAttachmentMenu && (
                  <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-2 z-10">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <span>Document</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>Photo & Video</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => audioInputRef.current?.click()}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                        />
                      </svg>
                      <span>Audio</span>
                    </button>
                  </div>
                )}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.xls,.xlsx,.doc,.docx,.ppt,.pptx,.pdf,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf"
              onChange={e => handleFileSelect(e, 'document')}
              className="hidden"
            />
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  const type = file.type.startsWith('image/')
                    ? 'image'
                    : 'video';
                  handleFileSelect(e, type);
                }
              }}
              className="hidden"
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              onChange={e => handleFileSelect(e, 'audio')}
              className="hidden"
            />

            {/* Message Input Field */}
            <div className="flex flex-1 relative items-center">
              <textarea
                ref={textareaRef}
                placeholder={
                  !isWithin24Hours
                    ? 'Outside 24-hour window - Use template message button →'
                    : attachmentType === 'audio'
                      ? 'Captions are not supported for audio'
                      : 'Type a message...'
                }
                value={newMessage}
                onChange={e => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                rows={1}
                disabled={!isWithin24Hours || attachmentType === 'audio'}
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none flex items-center ${
                  !isWithin24Hours || attachmentType === 'audio'
                    ? 'bg-gray-100 cursor-not-allowed'
                    : ''
                }`}
              />
            </div>

            {/* Emoji Button */}
            {isWithin24Hours && attachmentType !== 'audio' && (
              <button
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
            )}

            {/* Voice Message Button */}
            {isWithin24Hours &&
              attachmentType !== 'audio' &&
              (!isRecording ? (
                <button
                  type="button"
                  onMouseDown={startRecording}
                  onMouseUp={stopRecording}
                  className="p-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  onMouseUp={stopRecording}
                  className="p-2 text-red-500 focus:outline-none"
                >
                  <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs font-bold">
                      {formatRecordingTime(recordingTime)}
                    </span>
                  </div>
                </button>
              ))}

            {/* Send Button or Send Template Button */}
            {!isWithin24Hours ? (
              <button
                type="button"
                onClick={() => setShowTemplateMode(true)}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center space-x-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="text-sm font-medium">Send Template</span>
              </button>
            ) : (
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => setShowTemplateMode(true)}
                  className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 flex items-center space-x-2 transition-colors duration-200"
                  title="Send Template Message"
                >
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <span className="text-sm font-medium">Send Template</span>
                </button>
                <button
                  type="submit"
                  disabled={
                    (!newMessage.trim() && !selectedFile) || isUploading
                  }
                  className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 flex items-center justify-center"
                >
                  {isUploading ? (
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                      />
                    </svg>
                  )}
                </button>
              </div>
            )}
          </form>

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="mt-2 p-2 bg-gray-50 rounded-lg max-h-32 overflow-y-auto">
              <div className="grid grid-cols-8 gap-1">
                {[
                  '😀',
                  '😃',
                  '😄',
                  '😁',
                  '😆',
                  '😅',
                  '😂',
                  '🤣',
                  '😊',
                  '😇',
                  '🙂',
                  '🙃',
                  '😉',
                  '😌',
                  '😍',
                  '🥰',
                  '😘',
                  '😗',
                  '😙',
                  '😚',
                  '😋',
                  '😛',
                  '😝',
                  '😜',
                  '🤪',
                  '🤨',
                  '🧐',
                  '🤓',
                  '😎',
                  '🤩',
                  '🥳',
                  '😏',
                ].map((emoji, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      setNewMessage(prev => prev + emoji);
                      setShowEmojiPicker(false);
                    }}
                    className="w-8 h-8 text-lg hover:bg-gray-200 rounded"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Template Modal */}
      <SendTemplateModal
        isOpen={showTemplateMode}
        onClose={() => setShowTemplateMode(false)}
        templates={templates}
        onSend={onSendTemplateMessage}
        projectId={projectId}
      />
    </div>
  );
};

export default ChatConversation;
